import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search } from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { SearchOverlay } from "./SearchOverlay";

const PAGE_LINKS = [
  { label: "Books", to: "/books", testid: "nav-link-books" },
  { label: "Writings", to: "/writings", testid: "nav-link-blogs" },
  { label: "About", to: "/about", testid: "nav-link-about" },
  { label: "Contact", to: "/contact", testid: "nav-link-contact" },
];

export const Header = ({ theme, toggle }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ⌘K / Ctrl-K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearch((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <motion.header
        data-testid="header-navigation"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-[var(--bg)] ${
          scrolled || location.pathname !== "/"
            ? "border-b border-[var(--border-c)] shadow-[0_1px_0_rgba(0,0,0,0.03)]"
            : "border-b border-[var(--border-c)]/60"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 h-20 flex items-center justify-between">
          <Logo />

          <nav className="hidden md:flex items-center gap-9">
            {PAGE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className="font-sans text-sm text-[var(--text-2)] hover:text-[var(--text)] transition-colors duration-300 gold-underline py-1"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setSearch(true)}
              data-testid="header-search-button"
              aria-label="Open search"
              className="h-10 w-10 grid place-items-center rounded-full border border-[var(--border-c)] text-[var(--text)] hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors"
            >
              <Search className="h-4 w-4" />
            </button>
            <ThemeToggle theme={theme} toggle={toggle} />
            <Link
              to="/books"
              data-testid="header-cta-explore"
              className="hidden lg:inline-flex items-center rounded-full bg-[var(--ink)] text-[var(--bg)] px-5 py-2.5 font-sans text-sm font-medium hover:bg-[var(--terracotta)] transition-colors duration-300"
            >
              Explore Collection
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-[var(--border-c)] text-[var(--text)]"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden bg-[var(--bg)]/97 backdrop-blur-xl border-b border-[var(--border-c)]"
            >
              <div className="px-6 py-6 flex flex-col gap-4">
                {PAGE_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-serif text-2xl text-[var(--text)]"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <SearchOverlay open={search} onClose={() => setSearch(false)} />
    </>
  );
};
